import React, { useEffect, useState } from 'react';
import './CompanyDetails.css';
import NotificationSettingsSelect from "./NotificationSettingsSelect";
import { useCompanyContext } from '../hooks/useCompanyContext';
import { useLanguageContext } from "../hooks/useLanguageContext";
import { useCultivationCompanyContext } from "../hooks/useCultivationCompanyContext";
import { useAuthContext } from "../hooks/useAuthContext";
import translations from "../translations";
import UserAccessModal from './UserAccessModal';
import axios from 'axios';

const CompanyDetails = ({ webUsers = [], onUserUpdated }) => {
  const { language } = useLanguageContext();
  const { company } = useCompanyContext();
  const { user } = useAuthContext();
  const { cultivationCompany } = useCultivationCompanyContext();

  // ⬇️ Hooks ALTIJD bovenaan, vóór elke return
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const normEmail = (e) => String(e || "").toLowerCase().trim();

  // ✅ Optimistische lijst (zodat UI direct "Beheren" toont)
  const [localWebUsers, setLocalWebUsers] = useState(webUsers);

  useEffect(() => {
    setLocalWebUsers((prev) => {
      const map = new Map(
        (prev || []).map((u) => [normEmail(u.email), u])
      );
  
      (webUsers || []).forEach((u) => {
        const key = normEmail(u.email);
        if (!key) return;
        map.set(key, { ...map.get(key), ...u });
      });
  
      return Array.from(map.values());
    });
  }, [webUsers]);

  const findUserByEmail = (emailRaw) => {
    const email = (emailRaw || '').toLowerCase().trim();
    return localWebUsers.find((u) => (u.email || '').toLowerCase().trim() === email);
  };

  // Vanaf hier mag je conditioneel returnen
  if (!company || !company.companyinfo || company.companyinfo.length === 0) {
    return <p>Gegevens worden geladen…</p>;
  }

  const companyInfo = company.companyinfo[0];
  const contacts = company.contacts || [];
  const shippings = company.shipping_addresses || [];

    // simpele e-mail check
  const isValidEmail = (email) => {
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const createWebUser = async ({ name, email }) => {
    try {
      if (!company?.Companyid) throw new Error("Geen companyId beschikbaar");
      if (!email) throw new Error("Geen email opgegeven");
  
      const payload = {
        name,   // optioneel (wordt Naam)
        email,  // verplicht
      };
  
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/company/${company.Companyid}/create-user`,
        payload,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
  
      return res.data;
    } catch (e) {
      console.error("❌ Fout bij aanmaken/koppelen webuser:", e?.response?.data || e.message);
      throw e;
    }
  };

  const handleNoAccountClick = async (contact) => {
    const emailRaw = contact.E_Mail || contact.Email || contact.email || '';
    const email = (emailRaw || '').trim();
  
    if (!isValidEmail(email)) {
      alert('Er is geen geldig e-mailadres aanwezig voor deze contactpersoon.');
      return;
    }
  
    const confirmed = window.confirm(
      `Er is nog geen webaccount voor:\n\n${email}\n\nAccount aanmaken?`
    );
    if (!confirmed) return;
  
    // 1) Alleen de API-call in try/catch
    let resp;
    try {
      resp = await createWebUser({ name: contact.Name, email });
    } catch (e) {
      console.error("❌ createWebUser request faalde:", e?.response?.data || e.message);
      alert('Aanmaken mislukt. Controleer de server logs.');
      return;
    }
  
    // 2) Succes-melding: request is gelukt
    alert(`Account aangemaakt of gekoppeld aan ${email}.`);
  
    // 3) UI direct bijwerken (mag NIET kunnen crashen)
    try {
      const newEmail = String(resp?.email || email).toLowerCase().trim();
      const newId = resp?.userId || resp?._id; // vang beide varianten op
  
      // Als er geen id terugkomt, kunnen we nog steeds "Beheren" laten zien,
      // maar modal openen kan dan later pas na refresh.
      const optimisticUser = {
        _id: newId,
        email: newEmail,
        roles: ["gebruiker"],
      };
  
      setLocalWebUsers((prev) => {
        const exists = (prev || []).some(
          (u) => String(u?.email || "").toLowerCase().trim() === newEmail
        );
        return exists ? prev : [optimisticUser, ...(prev || [])];
      });
    } catch (uiErr) {
      // NIET als fout tonen aan gebruiker; account is al aangemaakt.
      console.warn("⚠️ UI optimistic update faalde, maar account is aangemaakt:", uiErr);
    }
  };
  

  const openManageForContact = (contact) => {
    const emailRaw = contact.E_Mail || contact.Email || contact.email || '';
    const matched = findUserByEmail(emailRaw);
    if (!matched) {
      alert('Deze contactpersoon heeft (nog) geen webaccount. Maak eerst een gebruiker aan.');
      return;
    }
    if (!matched?._id) {
      alert("Account is aangemaakt, maar details zijn nog niet geladen. Ververs even de pagina.");
      return;
    }
    setSelectedUser(matched);
    setModalOpen(true);
  };

  const handleSaved = (updatedUser) => {
    const updatedEmail = (updatedUser?.email || "").toLowerCase().trim();
    const updatedId = updatedUser?._id;
  
    setLocalWebUsers((prev) => {
      const list = prev || [];
      const idx = list.findIndex((u) =>
        (updatedId && u?._id && String(u._id) === String(updatedId)) ||
        normEmail(u?.email) === updatedEmail
      );
    
      if (idx === -1) {
        // 🔥 nieuw → toevoegen
        return [{ ...updatedUser }, ...list];
      }
    
      const copy = [...list];
      copy[idx] = { ...copy[idx], ...updatedUser };
      return copy;
    });
  
    onUserUpdated?.(updatedUser);
  };

  return (
    <div style={{ maxWidth: '75%' }}>
      <h3 className="pageheading">{translations[language].klantgegevens}</h3>
      <div className="grid" style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="row">
          <div className="col-xs-3 col-sm-3 col-md-3 col-lg-3">
            <p>{translations[language].naam}<br /></p>
          </div>
          <div className="col-xs-9 col-sm-9 col-md-9 col-lg-9">
            <p>{companyInfo.Name || 'N.v.t.'}<br /></p>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-3 col-sm-3 col-md-3 col-lg-3">
            <p>{translations[language].adres}<br /></p>
          </div>
          <div className="col-xs-9 col-sm-9 col-md-9 col-lg-9">
            <p>{companyInfo.Address || 'N.v.t.'}<br /></p>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-3 col-sm-3 col-md-3 col-lg-3">
            <p>{translations[language].postcode}<br /></p>
          </div>
          <div className="col-xs-9 col-sm-9 col-md-9 col-lg-9">
            <p>{companyInfo.Post_Code || 'N.v.t.'}<br /></p>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-3 col-sm-3 col-md-3 col-lg-3">
            <p>{translations[language].plaats}<br /></p>
          </div>
          <div className="col-xs-9 col-sm-9 col-md-9 col-lg-9">
            <p>{companyInfo.City || 'N.v.t.'}<br /></p>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-3 col-sm-3 col-md-3 col-lg-3">
            <p>{translations[language].telefoon}<br /></p>
          </div>
          <div className="col-xs-9 col-sm-9 col-md-9 col-lg-9">
            <p>{companyInfo.Phone_No || 'N.v.t.'}<br /></p>
          </div>
        </div>
      </div>

      {contacts.length > 0 && (
        <div>
          <h3 className="pageheading">{translations[language].contactpersonen}</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid black', padding: '8px' }}>{translations[language].naam}</th>
                <th style={{ border: '1px solid black', padding: '8px' }}>{translations[language].telefoon}</th>
                <th style={{ border: '1px solid black', padding: '8px' }}>{translations[language].email}</th>
                <th style={{ border: '1px solid black', padding: '8px', textAlign: 'center' }}>Webuser</th>
                <th style={{ border: '1px solid black', padding: '8px' }}>{translations[language].rollen}</th>
                {user.roles && user.roles.includes("medewerker") && (
                  <th style={{ border: '1px solid black', padding: '8px' }}></th>
                )}
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact, index) => {
                const emailRaw = contact.E_Mail || contact.Email || contact.email || '';
                const userMatch = findUserByEmail(emailRaw);
                const isWebuser = !!userMatch;
                const rollen = userMatch?.roles?.filter(r => r !== 'gebruiker')?.join(', ') || '';
                // const enabled = userMatch?.portalEnabled === true; // of .active

                return (
                  <tr key={index}>
                    <td style={{ border: '1px solid black', padding: '8px' }}>{contact.Name || 'N.v.t.'}</td>
                    <td style={{ border: '1px solid black', padding: '8px' }}>{contact.Phone_No || 'N.v.t.'}</td>
                    <td style={{ border: '1px solid black', padding: '8px' }}>{emailRaw || 'N.v.t.'}</td>
                    <td style={{ border: '1px solid black', padding: '8px', textAlign: 'center' }}>
                      {isWebuser && userMatch?.roles?.length > 0 ? '✓' : ''}
                    </td>
                    <td style={{ border: '1px solid black', padding: '8px' }}>
                      {rollen || (isWebuser ? 'Geen rollen' : '')}
                    </td>
                    {user.roles && user.roles.includes("medewerker") && (
                    <td style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                        {isWebuser && Array.isArray(userMatch?.companies) && userMatch.companies.length > 1 && (
                          <span
                            style={{
                              display: 'inline-block',
                              background: '#fef3c7',
                              color: '#92400e',
                              fontSize: 12,
                              fontWeight: 600,
                              padding: '4px 8px',
                              borderRadius: 999
                            }}
                            title={`Heeft toegang tot ${userMatch.companies.length} bedrijven`}
                          >
                            {userMatch.companies.length}
                          </span>
                        )}

                        <button
                          onClick={() => (isWebuser ? openManageForContact(contact) : handleNoAccountClick(contact))}
                          style={{
                            background: isWebuser ? '#0d6efd' : '#ccc',
                            color: isWebuser ? '#fff' : '#666',
                            border: 'none',
                            padding: '6px 10px',
                            borderRadius: 6
                          }}
                          title={isWebuser ? 'Rechten beheren' : 'Geen webaccount'}
                        >
                          {isWebuser ? 'Beheren' : 'Geen account'}
                        </button>
                      </div>
                    </td>
                  )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {Array.isArray(user?.roles) && user.roles.includes("medewerker") && (
        <NotificationSettingsSelect contacts={company?.contacts || []} />
      )}

      {shippings.length > 0 && (
        <div>
          <h3 className="pageheading">{translations[language].verzendAdres}</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid black', padding: '8px' }}>{translations[language].naam}</th>
                <th style={{ border: '1px solid black', padding: '8px' }}>{translations[language].adres}</th>
                <th style={{ border: '1px solid black', padding: '8px' }}>{translations[language].postcode}</th>
                <th style={{ border: '1px solid black', padding: '8px' }}>{translations[language].plaats}</th>
              </tr>
            </thead>
            <tbody>
              {shippings.map((shipping, index) => (
                <tr key={index}>
                  <td style={{ border: '1px solid black', padding: '8px' }}>{shipping.Name || 'N.v.t.'}</td>
                  <td style={{ border: '1px solid black', padding: '8px' }}>{shipping.Address || 'N.v.t.'}</td>
                  <td style={{ border: '1px solid black', padding: '8px' }}>{shipping.Post_Code || 'N.v.t.'}</td>
                  <td style={{ border: '1px solid black', padding: '8px' }}>{shipping.City || 'N.v.t.'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <UserAccessModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        user={selectedUser}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default CompanyDetails;